import "components/QuerySection/components/OCRStatusList.scss";

import Button from "components/Button/Button";
import OCRMSADevelopmentModal from "components/Modals/OCRMSADevelopmentModal";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import { msaAPI } from "services/msa-api";
import { useGetTextractRequestsByFileQuery } from "services/rre-profile-ingestion-api";
import type { BackgroundJobDataType } from "types/DataTypes";
import { useDispatch } from "utils/redux";

type MSADevelopmentOCRReviewProps = {
  msaId: string;
  fileId: number;
};

type Phase = "queued" | "processing" | "failed";

const isJobFailed = (job: BackgroundJobDataType) =>
  job.status === "Failed" || job.status === "Error";

const statusText: Record<Phase, string> = {
  queued: "Queued for OCR processing...",
  processing: "Processing OCR...",
  failed: "Something went wrong processing this letter.",
};

const MSADevelopmentOCRReview = ({
  msaId,
  fileId,
}: MSADevelopmentOCRReviewProps) => {
  const dispatch = useDispatch();
  const [dismissedJobIds, setDismissedJobIds] = useState<Set<number>>(
    new Set(),
  );
  const [retryingJobId, setRetryingJobId] = useState<number | null>(null);

  const [jobsPollingInterval, setJobsPollingInterval] = useState(3000);
  const { data: jobsResponse } = useGetBackgroundJobsQuery(
    { filters: { msa_id: msaId } },
    { pollingInterval: jobsPollingInterval },
  );

  const allDevelopmentJobs = useMemo(
    () =>
      (jobsResponse?.results || []).filter(
        (j) => j.job_type_value === "ocr_msa_development",
      ),
    [jobsResponse],
  );
  // Fires once per job the first time it's seen as succeeded, not just once
  // ever: a plain "did any job succeed" boolean stays true forever once the
  // first job here ever succeeds (old jobs never leave this list), so a
  // second job succeeding later would never trigger another refetch and its
  // card would only ever show up after a manual page reload.
  const notifiedSuccessJobIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const newlySucceeded = allDevelopmentJobs.filter(
      (j) =>
        j.status === "Success" && !notifiedSuccessJobIdsRef.current.has(j.id),
    );
    if (newlySucceeded.length > 0) {
      newlySucceeded.forEach((j) => notifiedSuccessJobIdsRef.current.add(j.id));
      dispatch(msaAPI.util.invalidateTags(["MSA"]));
    }
  }, [allDevelopmentJobs, dispatch]);

  // Dropped the instant a job succeeds, rather than waiting to confirm its
  // card shows up on the page first: that confirmation could be missed
  // entirely (the card gets deleted before this widget is ever open to see
  // it), which left a succeeded job stuck showing "done" forever with
  // nothing left to point to. Once a job succeeds this widget has nothing
  // further to say about it; the real card (or its absence) speaks for
  // itself elsewhere on the page.
  const developmentJobs = useMemo(
    () => allDevelopmentJobs.filter((j) => j.status !== "Success"),
    [allDevelopmentJobs],
  );

  const visibleJobs = useMemo(
    () => developmentJobs.filter((j) => !dismissedJobIds.has(j.id)),
    [developmentJobs, dismissedJobIds],
  );

  // Multiple letters can be mid-scan at once, since developments aren't
  // mutually exclusive the way decisions are, so every job that hasn't
  // resolved yet (or was just dismissed) gets its own row instead of only
  // showing the single most relevant one.
  const anyJobUnresolved = developmentJobs.some((j) => !isJobFailed(j));

  useEffect(() => {
    setJobsPollingInterval(anyJobUnresolved ? 3000 : 0);
  }, [anyJobUnresolved]);

  const { data: textractRequests } = useGetTextractRequestsByFileQuery(
    { fileId, jobType: "msa_development" },
    {
      skip: visibleJobs.length === 0,
      pollingInterval: anyJobUnresolved ? 3000 : 0,
    },
  );

  const getPhase = (job: BackgroundJobDataType): Phase => {
    if (isJobFailed(job)) return "failed";
    const ownTextractRequest = job.textract_request_id
      ? textractRequests?.find((r) => r.id === job.textract_request_id)
      : null;
    return !ownTextractRequest || ownTextractRequest.status === "pending"
      ? "queued"
      : "processing";
  };

  const handleCloseModal = () => {
    if (retryingJobId != null) {
      const job = developmentJobs.find((j) => j.id === retryingJobId);
      if (job && isJobFailed(job)) {
        setDismissedJobIds((prev) => new Set(prev).add(retryingJobId));
      }
    }
    setRetryingJobId(null);
  };

  if (visibleJobs.length === 0) {
    return null;
  }

  return (
    <>
      {retryingJobId != null && (
        <OCRMSADevelopmentModal
          isOpen
          onClose={handleCloseModal}
          msaId={msaId}
        />
      )}
      <div className="ocr-status-list">
        <div className="data-block">
          <div className="data-block-header">
            <span className="material-icons ocr-status-list__header-icon">
              document_scanner
            </span>
            CMS Development Letter OCR
          </div>
          <div className="ocr-status-list__items">
            {visibleJobs.map((job) => {
              const phase = getPhase(job);
              const isActive = phase === "queued" || phase === "processing";
              const statusKey = phase === "failed" ? "failed" : "in_progress";
              const icon = phase === "failed" ? "error" : "document_scanner";

              return (
                <div
                  key={job.id}
                  className={`ocr-status-item ocr-status-item--${statusKey}`}
                >
                  <div className="ocr-status-item__icon-wrapper">
                    <span
                      className={`material-icons ocr-status-item__icon ${isActive ? "ocr-status-item__icon--pulse" : ""}`}
                    >
                      {icon}
                    </span>
                  </div>
                  <div className="ocr-status-item__content">
                    <div className="ocr-status-item__status-text">
                      {phase === "failed"
                        ? job.status_message || statusText.failed
                        : statusText[phase]}
                    </div>
                  </div>
                  {phase === "failed" && (
                    <Button onClick={() => setRetryingJobId(job.id)}>
                      Try Again
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default MSADevelopmentOCRReview;
